import { useState } from 'react';
import { Flag, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';

const ISSUES = [
  { key: 'wrong_image', label: 'Wrong image or animation' },
  { key: 'wrong_description', label: 'Wrong description' },
  { key: 'wrong_muscle', label: 'Wrong muscle group' },
  { key: 'wrong_equipment', label: 'Wrong equipment' },
  { key: 'media_broken', label: "Image or video won't load" },
];

/** Little flag-on-a-stick over the exercise media — opens a one-tap report sheet. */
export const ExerciseFlagButton = ({
  exercise, primaryMuscle, equipment, planId, dayIndex, source, testId = 'exercise-flag-btn',
}) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(null);
  const [note, setNote] = useState('');

  const send = async (issue) => {
    if (sending) return;
    setSending(issue);
    try {
      const res = await api.post('/reports/exercise', {
        exercise_name: exercise,
        issue,
        note: issue === 'other' ? note : (note || null),
        primary_muscle: primaryMuscle || null,
        equipment: equipment || null,
        plan_id: planId || null,
        day_index: typeof dayIndex === 'number' ? dayIndex : null,
        source: source || null,
      });
      toast.success(`Thanks — logged as ${res.data.ref_code}`);
      setOpen(false);
      setNote('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the report');
    } finally {
      setSending(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        aria-label={`Report a problem with ${exercise}`}
        data-testid={testId}
        className="absolute bottom-2 left-2 z-20 w-9 h-9 rounded-full flex items-center justify-center bg-black/45 backdrop-blur-sm text-white/80 hover:text-white active:scale-[0.85] transition-[transform,color]"
      >
        <Flag size={16} strokeWidth={2.5} />
      </button>

      <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setNote(''); }}>
        <SheetContent side="bottom"
          className="bg-[#07090C] border-t border-white/10 rounded-t-3xl max-h-[88vh] overflow-y-auto pb-[calc(6.5rem+env(safe-area-inset-bottom,0px))]"
          data-testid="exercise-report-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="font-heading font-black uppercase tracking-tight text-white flex items-center gap-2">
              <Flag size={16} className="text-white/50" /> Report this exercise
              <span className="block w-full text-[11px] font-normal normal-case tracking-normal text-white/45"
                    data-testid="exercise-report-name">{exercise}</span>
            </SheetTitle>
          </SheetHeader>

          <p className="mt-3 text-[11px] uppercase tracking-[0.12em] text-white/40">Tap what's wrong — sends straight away</p>
          <div className="mt-2 space-y-2">
            {ISSUES.map(({ key, label }) => (
              <button key={key} disabled={!!sending} onClick={() => send(key)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.04] text-left font-heading font-bold text-sm text-white active:scale-[0.98] transition-transform disabled:opacity-50"
                data-testid={`exercise-report-${key}`}>
                {label}
                {sending === key && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
              </button>
            ))}
          </div>

          <p className="mt-5 text-[11px] uppercase tracking-[0.12em] text-white/40">Or tell us in your own words</p>
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000}
            placeholder="What's wrong with this exercise?"
            className="mt-2 w-full rounded-xl bg-white/[0.04] border border-white/10 p-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/25"
            data-testid="exercise-report-note" />
          <button disabled={!!sending || !note.trim()} onClick={() => send('other')}
            className="mt-2 w-full py-3 rounded-xl font-heading font-bold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ background: '#0A84FF' }}
            data-testid="exercise-report-submit">
            {sending === 'other' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send report
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ExerciseFlagButton;
